import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Tooltip,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";
import ReactDOMServer from "react-dom/server";

import { PRICING_TABLE } from "../../../constants/editor";
import { Icon } from "../../ui/icon";
import { Input } from "../../ui/input";
import Modal from "../../ui/modal";

import global from "../../../scss/dhp.scss";
import { widgetConfig } from "../editor_config";
import {
  fetchProposalItems,
  fetchProposalModuleList,
  setUsedProposalModule,
} from "../../../store/actions/companyActions";
import TaxInputDropdown from "../../ui/TaxInputDropdown";
import useProposalCalculator from "../../../hooks/useProposalCalculator";
import useDragAndDrop from "../../../hooks/useDragAndDropV2";
import useAlignment from "../../../hooks/useAlignment";
import useAddWidget from "../../../hooks/useAddWidget";
import useElementInnerHtml from "../../../hooks/useElementInnerHtml";
import { EditorContext } from "../../../containers/editor/EditorLayout";
import { useUndoRedo } from "../../../hooks/useUndoRedo";
import { generateUniqueHash, hexToRGB } from "../../../_helpers/utils";
import AssetField from "../../ui/AssetField";
import { uploadPricingItemAsset } from "../../../store/actions/documentActions";
import ModalFlashMessage from "../../ui/ModalFlashMessage";

let style = Object.assign({}, global);

const ITEM = "item";
const SECTION = "section";

const billingSuffixes = {
  "One-time": "",
  Weekly: " / Week",
  Monthly: " / Month",
  Quarterly: " / Quarter",
  "Half-yearly": " / Half-year",
  Yearly: " / Year",
};

const billingFrequencyOptions = [
  { name: "One-time" },
  { name: "Weekly" },
  { name: "Monthly" },
  { name: "Quarterly" },
  { name: "Half-yearly" },
  { name: "Yearly" },
];

const normalizeUsageUnitForUI = (usageUnit, usageUnitList) => {
  if (!usageUnit) return "";

  // if new record
  const matched = usageUnitList.find(u => u._id === usageUnit);
  if (matched) return matched.name;

  // for Old record
  return usageUnit;
};

const InputFieldSection = ({ type, sectionIdx, itemIdx, checked = false }) => {
  const fieldFor = typeof itemIdx === "number" ? ITEM : SECTION;
  const fieldId =
    fieldFor === ITEM ? `custom-item${type}-s${sectionIdx}-i${itemIdx}` : `custom-section${type}-s${sectionIdx}`;
  const fieldName =
    fieldFor === ITEM ? `pricing-item${type}-s${sectionIdx}-i${itemIdx}` : `pricing-section${type}-s${sectionIdx}`;
  return (
    <div className={cx(style["radio-icon"], style["form-group"], style["mb-0"])}>
      <div
        className={cx(style["custom-control"], style["custom-control-inline"], style["form-check"], {
          [style["custom-radio"]]: type == "radio",
          [style["custom-checkbox"]]: type == "checkbox",
        })}>
        <input
          type={type}
          id={fieldId}
          name={fieldName}
          checked={checked}
          className={`form-check-input ${
            fieldFor === ITEM ? "pricing-table-item-input" : "pricing-table-section-input"
          }`}
        />
        <label className="form-check-label"></label>
      </div>
    </div>
  );
};

const CanvasHtmlData = ({ canvasConfigData, formattedCurrency, usageUnitData }) => {
  const sectionHeaderStyle = {};
  if (canvasConfigData?.sectionHeaderColor) {
    sectionHeaderStyle.backgroundColor = hexToRGB(canvasConfigData.sectionHeaderColor);
  }
  if (canvasConfigData?.sectionBorderColor) {
    sectionHeaderStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.sectionBorderColor)}`;
  }

  const sectionSubHeaderStyle = {};
  if (canvasConfigData?.sectionSubheaderColor) {
    sectionSubHeaderStyle.backgroundColor = hexToRGB(canvasConfigData.sectionSubheaderColor);
  }
  if (canvasConfigData?.sectionBorderColor) {
    sectionSubHeaderStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.sectionBorderColor)}`;
  }

  const sectionBodyStyle = {};
  if (canvasConfigData?.sectionBodyColor) {
    sectionBodyStyle.backgroundColor = hexToRGB(canvasConfigData.sectionBodyColor);
  }
  if (canvasConfigData?.sectionBorderColor) {
    sectionBodyStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.sectionBorderColor)}`;
  }
  if (canvasConfigData?.sectionBodyContentColor) {
    sectionBodyStyle.color = hexToRGB(canvasConfigData.sectionBodyContentColor);
  }

  const grandTotalBodyStyle = {};
  if (canvasConfigData?.grandTotalBodyColor) {
    grandTotalBodyStyle.backgroundColor = hexToRGB(canvasConfigData.grandTotalBodyColor);
  }
  if (canvasConfigData?.grandTotalBorderColor) {
    grandTotalBodyStyle.border = `1px solid ${hexToRGB(canvasConfigData.grandTotalBorderColor)}`;
  }

  const grandTotalBorderStyle = {};
  if (canvasConfigData?.grandTotalBorderColor) {
    grandTotalBorderStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.grandTotalBorderColor)}`;
  }

  // Content Color section
  const sectionHeaderContentStyle = {};
  if (canvasConfigData?.grandTotalBorderColor) {
    sectionHeaderContentStyle.color = hexToRGB(canvasConfigData.sectionHeaderContentColor);
  }

  const sectionSubheaderContentStyle = {};
  if (canvasConfigData?.grandTotalBorderColor) {
    sectionSubheaderContentStyle.color = hexToRGB(canvasConfigData.sectionSubheaderContentColor);
  }

  const grandTotalBodyContentStyle = {};
  if (canvasConfigData?.grandTotalBorderColor) {
    grandTotalBodyContentStyle.color = hexToRGB(canvasConfigData.grandTotalBodyContentColor);
  }
  return (
    <div
      className={cx(style["w-100"], style["pricingTable_editor"])}
      style={canvasConfigData?.["font-family"] ? { fontFamily: canvasConfigData["font-family"] } : {}}>
      {canvasConfigData?.sections?.map((sectionInfo, sectionIdx) => (
        <div
          className={`mb-3 card pricing-table-section section-card-${sectionIdx}`}
          key={sectionIdx}
          style={
            canvasConfigData?.sectionBorderColor ? { border: `1px solid ${canvasConfigData.sectionBorderColor}` } : {}
          }>
          <div
            className={cx(
              style["d-flex"],
              style["justify-content-between"],
              style["align-items-center"],
              style["card-header"]
            )}
            style={sectionHeaderStyle}>
            <div
              className={cx(style["d-flex"], style["align-items-center"], {
                [style["canvas-pricing-table-inputbox"]]:
                  canvasConfigData?.optionalSection || sectionInfo?.optionalSection,
              })}>
              {(canvasConfigData?.optionalSection || sectionInfo?.optionalSection) && (
                <>
                  {(canvasConfigData?.sectionSelectionType === "multiple" || sectionInfo?.optionalSection) && (
                    <InputFieldSection type="checkbox" sectionIdx={sectionIdx} checked={sectionInfo?.active} />
                  )}
                  {canvasConfigData?.sectionSelectionType === "single" && !sectionInfo?.optionalSection && (
                    <InputFieldSection type="radio" sectionIdx={sectionIdx} checked={sectionInfo?.active} />
                  )}
                </>
              )}
              <h6
                className={cx(style["mb-0"], style["fw-7"], {
                  [style["ms-4"]]: !canvasConfigData?.optionalSection && !sectionInfo?.optionalSection,
                })}
                style={sectionHeaderContentStyle}>
                {sectionInfo.name}
              </h6>
            </div>
            <h6
              className={`mb-0 fw-7 d-flex pricing-amount ${!sectionInfo.active ? "disable" : ""}`}
              style={sectionHeaderContentStyle}>
              <span className={style["pe-2"]}>Total</span>

              <div className={"section-total"}>{sectionInfo.total}</div>
            </h6>
          </div>
          <div className={cx(style["p-0"], style["card-body"])}>
            <div className={cx(style["item-label"])} style={sectionSubHeaderStyle}>
              <ul className={cx(style["d-flex"], style["align-items-center"])}>
                <li
                  className={cx(style["item-name-lable"], style["flex-grow-1"], style["ms-4"])}
                  style={sectionSubheaderContentStyle}>
                  Items
                </li>
                <li className={cx(style["item-quantity-lable"])} style={sectionSubheaderContentStyle}>
                  Quantity
                </li>
                <li
                  className={cx(style["item-amount-lable"], style["text-right"])}
                  style={sectionSubheaderContentStyle}>
                  Amount
                </li>
              </ul>
            </div>
            {sectionInfo?.items?.map((itemInfo, itemIdx) => (
              <div
                className={cx(style["item-details"], { [style["no-image"]]: !itemInfo?.data?.images?.length > 0 })}
                key={itemIdx}
                style={sectionBodyStyle}>
                <div className={cx(style["item-info"])}>
                  <ul className={cx(style["d-flex"])}>
                    <li className="">
                      {(sectionInfo?.optionalItem || itemInfo?.optionalItem) && (
                        <>
                          {(sectionInfo?.itemSelectionType === "multiple" ||
                            (itemInfo.optionalItem && itemInfo.itemSelectionType === "multiple")) && (
                            <InputFieldSection
                              type="checkbox"
                              sectionIdx={sectionIdx}
                              itemIdx={itemIdx}
                              checked={itemInfo?.active}
                            />
                          )}
                          {sectionInfo?.itemSelectionType === "single" && (
                            <InputFieldSection
                              type="radio"
                              sectionIdx={sectionIdx}
                              itemIdx={itemIdx}
                              checked={itemInfo?.active}
                            />
                          )}
                        </>
                      )}
                    </li>
                    <li className={cx(style["item-info-details"], style["flex-grow-1"])}>
                      <div className={cx(style["d-flex"], style["mb-2"], style["align-items-center"])}>
                        {itemInfo?.data?.images?.length > 0 && (
                          <div
                            className={cx(style["item-image"], style["me-3"])}
                            data-image-urls={itemInfo?.data?.images}>
                            <figure className={cx(style["item-thumbnail"])}>
                              <img src={itemInfo.data.images[0]} className={cx(style["img-fluid"])} />
                            </figure>

                            {itemInfo?.data?.images?.length > 1 && (
                              <span className={style["count"]}>+{itemInfo?.data?.images?.length - 1}</span>
                            )}
                          </div>
                        )}
                        <span className={cx(style["fw-7"], style["item-name"])}>{itemInfo?.data?.item_name?.name}</span>
                      </div>
                      <div>
                        {itemInfo?.data?.item_code && canvasConfigData?.showItemCode && (
                          <p className={style["mb-1"]}>
                            <span className={cx(style["fw-7"], style["me-1"])}>Item Code: </span>{" "}
                            {itemInfo.data.item_code}
                          </p>
                        )}
                        {itemInfo?.data?.description && (
                          <p className={cx(style["mb-1"], style["pre-line-text"])}>{itemInfo.data.description}</p>
                        )}
                        <p className={cx(style["mb-1"])}>
                          <span className={cx(style["fw-7"], style["me-1"])}>Unit Price:</span>
                          <span>
                            {formattedCurrency(itemInfo.data?.unit_price)}
                            {itemInfo?.data?.usage_unit && (
                              <span> / {normalizeUsageUnitForUI(itemInfo.data.usage_unit, usageUnitData)}</span>
                            )}
                          </span>
                        </p>
                        {itemInfo?.data?.discount > 0 && canvasConfigData?.lineItemDiscount && (
                          <p className={style["mb-1"]}>
                            <span className={cx(style["fw-7"], style["me-1"])}>Discount:</span>{" "}
                            {(itemInfo?.data?.discountType === "%" &&
                              `${itemInfo?.data?.discount}${itemInfo.data.discountType}`) ||
                              `${itemInfo.data.discountType} ${itemInfo?.data?.discount} `}
                          </p>
                        )}
                        {itemInfo?.data?.tax.length > 0 && (
                          <p className={cx(style["mb-1"], style["tax-info"])}>
                            <span className={cx(style["fw-7"], style["me-1"])}>Tax:</span>
                            {itemInfo.data.tax.map((taxData, tIdx, array) => (
                              <span key={tIdx}>
                                {taxData}
                                {tIdx < array.length - 1 ? ", " : ""}
                              </span>
                            ))}
                          </p>
                        )}
                      </div>
                    </li>
                    <li className={cx(style["item-quantity-details"], style["mt-2"])}>
                      {}
                      {!itemInfo?.editableQuantity && itemInfo?.data?.quantity}

                      {itemInfo?.editableQuantity && (
                        <div className={cx(style["input-spinner"], style["form-group"])}>
                          <div className={style["input-area"]}>
                            <div className={style["input-group"]}>
                              <div className={style["input-group-prepend"]}>
                                <button
                                  type="button"
                                  className={`btn-minus btn-decrement btn btn-outline-secondary item-${itemIdx}`}>
                                  <strong>-</strong>
                                </button>
                              </div>
                              <div className={style["form-control"]}> {itemInfo.data.quantity} </div>
                              <div className={style["input-group-append"]}>
                                <button
                                  type="button"
                                  className={`btn-plus btn-increment btn btn-outline-secondary item-${itemIdx}`}>
                                  <strong>+</strong>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                    <li className={"item-amount-details text-right mt-2"}>
                      <div className={`pricing-amount ${!itemInfo?.active ? "disable" : ""}`}>
                        {itemInfo.data?.amount}
                      </div>
                      <div className={cx(style["font-13"], style["text-muted"], style["text-right"])}>
                        {billingSuffixes?.[itemInfo?.data?.billing_frequency]}
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className={"card pricing-table-total"} style={grandTotalBodyStyle}>
        <div
          className={cx(
            style["d-flex"],
            style["justify-content-between"],
            style["align-items-center"],
            style["card-header"]
          )}
          style={grandTotalBorderStyle}>
          <h6 className={cx(style["mb-0"], style["ms-4"])} style={grandTotalBodyContentStyle}>
            {" "}
            Subtotal
          </h6>
          <h6 className={cx(style["mb-0"])} style={grandTotalBodyContentStyle}>
            <div className="grand-subtotal">{canvasConfigData.subTotal}</div>
          </h6>
        </div>
        {canvasConfigData.oneLineDiscount && canvasConfigData.oneLineDiscountValue > 0 && (
          <div
            className={cx(
              style["d-flex"],
              style["justify-content-between"],
              style["align-items-center"],
              style["card-header"]
            )}
            style={grandTotalBorderStyle}>
            <h6 className={cx(style["mb-0"], style["ms-4"])} style={grandTotalBodyContentStyle}>
              Discount &nbsp;
              {canvasConfigData.oneLineDiscountType == "%" && (
                <span>
                  {canvasConfigData.oneLineDiscountValue}
                  {canvasConfigData.oneLineDiscountType}
                </span>
              )}
            </h6>
            <h6 className={cx(style["mb-0"])} style={grandTotalBodyContentStyle}>
              <div className="grand-discount">- {canvasConfigData.oneLineDiscountAmount}</div>
            </h6>
          </div>
        )}
        {canvasConfigData?.detailedTaxes && (
          <div id="pricing-table-taxes">
            {canvasConfigData?.detailedTaxes?.map((taxInfo, taxIdx) => (
              <div
                key={taxIdx}
                className={cx(
                  style["d-flex"],
                  style["justify-content-between"],
                  style["align-items-center"],
                  style["card-header"]
                )}
                style={grandTotalBorderStyle}>
                <h6 className={cx(style["mb-0"], style["ms-4"])} style={grandTotalBodyContentStyle}>
                  {" "}
                  {taxInfo.name}
                </h6>
                <h6 className={cx(style["mb-0"])} style={grandTotalBodyContentStyle}>
                  <div className={`tax-amount-${taxIdx}`}>{taxInfo?.amount}</div>
                </h6>
              </div>
            ))}
          </div>
        )}

        <div
          className={cx(
            style["d-flex"],
            style["justify-content-between"],
            style["align-items-center"],
            style["card-header"],
            style["last-child"]
          )}
          style={grandTotalBorderStyle}>
          <h6 className={cx(style["mb-0"], style["fw-7"], style["ms-4"])} style={grandTotalBodyContentStyle}>
            Grand Total
          </h6>
          <h6 className={cx(style["mb-0"], style["fw-7"])} style={grandTotalBodyContentStyle}>
            <div className={"grand-total"}>{canvasConfigData?.grandTotal}</div>
          </h6>
        </div>
      </div>
    </div>
  );
};

const AutoResizingTextarea = ({
  inputFieldValue,
  handleBlur,
  handleChange,
  slug,
  sectionIdx,
  itemIdx,
  addSectionItem,
  setIsDescriptionFocused,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef(null);

  // Set initial and max heights
  const defaultHeight = 100;
  const minHeight = 48;
  const maxHeight = 200;

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to min when not focused
      if (!isFocused) {
        textareaRef.current.style.height = `${minHeight}px`;
        textareaRef.current.style.minHeight = `${minHeight}px`;
        textareaRef.current.scrollTop = 0; // Ensure scroll is at top
      } else {
        // Set to default height when focused, then allow auto-grow
        textareaRef.current.style.height = `${defaultHeight}px`;
        adjustHeight();
      }
    }
  }, [isFocused]);

  const adjustHeight = () => {
    if (textareaRef.current) {
      // Temporarily set to default height to measure scrollHeight accurately
      textareaRef.current.style.height = `${defaultHeight}px`;

      const scrollHeight = textareaRef.current.scrollHeight;
      let newHeight = Math.min(scrollHeight, maxHeight);
      if (newHeight < maxHeight) newHeight = newHeight + 5;
      // Apply the new height
      textareaRef.current.style.height = `${newHeight < defaultHeight ? defaultHeight : newHeight}px`;
    }
  };

  const handleFocus = () => {
    setIsDescriptionFocused(true);
    setIsFocused(true);
  };

  const handleLocalBlur = e => {
    if (textareaRef.current) {
      textareaRef.current.scrollTop = 0; // Scroll to top on blur
    }

    setIsFocused(false);
    setIsDescriptionFocused(false);
    if (handleBlur) {
      handleBlur(slug, sectionIdx, itemIdx);
    }

    const rawNext = e.relatedTarget;
    const nextEl = rawNext instanceof HTMLElement ? rawNext : null;
    const addTaskEl = nextEl?.closest?.('[data-role="add-item"]');
    const addSectionEl = nextEl?.closest?.('[data-role="add-section"]');

    if (addTaskEl) addSectionItem("item", sectionIdx);
    if (addSectionEl) addSectionItem("section");
  };

  const handleLocalChange = e => {
    adjustHeight();
    if (handleChange) {
      handleChange(e);
    }
  };

  return (
    <textarea
      ref={textareaRef}
      className={style["form-control"]}
      rows="3"
      value={inputFieldValue || ""}
      onFocus={handleFocus}
      onBlur={handleLocalBlur}
      onChange={handleLocalChange}
      style={{
        height: `${minHeight}px`,
        maxHeight: `${maxHeight}px`,
      }}
      {...props}
    />
  );
};

const ItemFields = ({
  itemValue,
  slug,
  sectionIdx,
  itemIdx,
  updateSectionValue,
  updateTableConfig,
  isEnable = true,
  fieldType = "text",
  maxLength,
  dropdownContainer,
  currData,
  maxMinLimt,
  addSectionItem,
  setIsDescriptionFocused,
  ...props
}) => {
  const inputRef = useRef(null);

  const [inputFieldValue, setInputFieldValue] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const resetValue = 100;

  const handleChange = e => {
    // Allow changes if:
    // 1. No maxLength is set, OR
    // 2. The new value is shorter (deletion), OR
    // 3. The new value length is within maxLength
    // if (maxLength && e.target.value.length > maxLength) return;

    // const value = e.target.value;
    let value = e.target.value;

    // Trim value to maxLength if it exceeds
    if (maxLength && value.length > maxLength) {
      value = value.substring(0, maxLength);
    }

    // Check for more than 2 decimal places
    if (["quantity", "discount"].includes(slug)) {
      // Check if value contains a decimal point and has more than 2 digits after it
      if (value.includes(".") && value.split(".")[1]?.length > 2) {
        return; // Prevent the update
      }

      const modifyValue = parseFloat(value);
      setInputFieldValue(modifyValue);
    } else {
      if (["unit_price", "minQty", "maxQty"].includes(slug)) {
        // Check if value contains a decimal point and has more than 2 digits after it
        if (value.includes(".") && value.split(".")[1]?.length > 2) {
          return; // Prevent the update
        }
      }
      setInputFieldValue(value);
    }

    // let modifyValue = ["quantity", "discount"].includes(slug) ? parseFloat(e.target.value) : e.target.value;
    // setInputFieldValue(modifyValue);
  };
  const handleBlur = (slug, sectionIdx, itemIdx) => {
    if (itemValue === inputFieldValue) return;
    let appliedValue = inputFieldValue;

    // Apply the final value on blur
    if (slug === "description") {
      appliedValue = appliedValue?.trim();
      setInputFieldValue(appliedValue);
    }

    if (slug === "quantity") {
      if (isNaN(inputFieldValue)) {
        appliedValue = 0;
        setInputFieldValue(0);
      }

      if (props.showAddonButton && maxMinLimt) {
        if (parseInt(appliedValue) > maxMinLimt.maxQty) {
          setInputFieldValue(maxMinLimt.maxQty);
          appliedValue = maxMinLimt.maxQty;
        }
        if (parseInt(appliedValue) < maxMinLimt.minQty) {
          setInputFieldValue(maxMinLimt.minQty);
          appliedValue = maxMinLimt.minQty;
        }
      }
    }
    if (slug === "discount" && parseInt(appliedValue) > resetValue && currData?.discountType === "%") {
      setInputFieldValue(resetValue);
      appliedValue = resetValue;
    }

    if (
      slug === "oneLineDiscountValue" &&
      parseInt(appliedValue) > resetValue &&
      currData?.oneLineDiscountType === "%"
    ) {
      setInputFieldValue(resetValue);
      appliedValue = resetValue;
    }

    if (typeof sectionIdx === "number" && typeof itemIdx === "number") {
      updateSectionValue({ [slug]: appliedValue }, sectionIdx, itemIdx);
    } else {
      if (slug === "oneLineDiscountValue") updateTableConfig({ oneLineDiscountValue: parseInt(appliedValue) });
    }
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setTooltipOpen(false); // Hide tooltip immediately on focus
  };

  const changeDiscountType = discountType => {
    if (typeof sectionIdx === "number" && typeof itemIdx === "number") {
      if (discountType === "%" && currData.discount > resetValue) {
        setInputFieldValue(resetValue);
        updateSectionValue({ discount: resetValue, discountType: discountType }, sectionIdx, itemIdx);
      } else updateSectionValue({ discountType: discountType }, sectionIdx, itemIdx);
    } else {
      if (discountType === "%" && currData.oneLineDiscountValue > resetValue) {
        setInputFieldValue(resetValue);
        updateTableConfig({ oneLineDiscountValue: resetValue, oneLineDiscountType: discountType });
      } else updateTableConfig({ oneLineDiscountType: discountType });
    }
  };

  const handleMouseEnter = () => {
    if (inputRef.current && !isFocused) {
      setTooltipOpen(inputRef.current.scrollWidth > inputRef.current.clientWidth);
    }
  };

  useEffect(() => {
    if (isEnable) setInputFieldValue(itemValue);
    else setInputFieldValue("");
  }, [itemValue, isEnable]);

  return (
    <>
      {fieldType != "textarea" && (
        <>
          <Input
            type={fieldType}
            value={inputFieldValue}
            onChange={e => handleChange(e)}
            onFocus={handleFocus}
            onBlur={e => handleBlur(slug, sectionIdx, itemIdx)}
            disabled={!isEnable}
            isEnable={!isEnable}
            {...(fieldType === "discount" && { changeDiscountType })}
            {...props}
            innerRef={inputRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setTooltipOpen(false)}
            onMouseDown={() => setTooltipOpen(false)}
            dropdownContainer={dropdownContainer ? dropdownContainer : undefined}
          />
          <Tooltip isOpen={tooltipOpen} target={inputRef.current} placement={"bottom-end"}>
            {inputFieldValue}
          </Tooltip>
        </>
      )}
      {fieldType === "textarea" && (
        <AutoResizingTextarea
          inputFieldValue={inputFieldValue}
          slug={slug}
          sectionIdx={sectionIdx}
          itemIdx={itemIdx}
          handleBlur={handleBlur}
          handleChange={handleChange}
          addSectionItem={addSectionItem}
          setIsDescriptionFocused={setIsDescriptionFocused}
          {...props}
        />
      )}
    </>
  );
};

const ToggleDropdownItem = ({ toggleId, label, selectfn, checked = false, disabled = false }) => {
  return (
    <>
      <div className={cx(style["form-check"], style["form-switch"], { [style["disable"]]: disabled })}>
        <input
          type="checkbox"
          className={style["form-check-input"]}
          defaultChecked={checked}
          id={toggleId}
          onClick={selectfn}
          disabled={disabled}
        />
        <label className={cx(style["form-check-label"], style["font-weight-normal"])} htmlFor={toggleId}>
          {label}
        </label>
      </div>
    </>
  );
};

const ItemDropdown = ({
  tableConfigData,
  updateTableConfig,
  sectionIdx,
  itemIdx,
  deleteSectionItem,
  itemInfo,
  updateSectionValue,
  dropdownContainer,
  customDropdownMenuClass,
}) => {
  const dropdownRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [showFrequencyUpdateField, setShowFrequencyUpdateField] = useState(false);
  const [defaultBillingFrequency, setDefaultBillingFrequency] = useState();

  // Toggle the dropdown
  const toggle = e => {
    if (e.target.closest(".custom-pricing-dropdown")) setIsOpen(true);
    else setIsOpen(prevState => !prevState);
  };

  const toggleBillingFrequincyOption = () => {
    setShowFrequencyUpdateField(prevState => !prevState);
  };

  const updateBillingFrequency = frequency => {
    updateSectionValue({ billing_frequency: frequency.name }, sectionIdx, itemIdx);
  };

  useEffect(() => {
    if (itemInfo.data?.billing_frequency && itemInfo.data?.billing_frequency != "") {
      setDefaultBillingFrequency({ name: itemInfo.data.billing_frequency });
    } else {
      setDefaultBillingFrequency(billingFrequencyOptions[0]);
    }
  }, [itemInfo.data?.billing_frequency]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const currentDropdown = document.querySelectorAll(".custom-pricing-dropdown.show")[0];
        if (currentDropdown) {
          // Check if menu has placed top of the dropdown and container present
          if (dropdownRef?.current && dropdownRef.current?.menuRef?.current && dropdownContainer) {
            const parentDropdown = dropdownRef.current.containerRef.current;
            const menuElement = dropdownRef.current.menuRef.current;

            const parentDropdownStyle = parentDropdown.getBoundingClientRect();
            const menuElementStyle = menuElement.getBoundingClientRect();
            if (menuElementStyle?.top < parentDropdownStyle?.top) {
              let calculatedTop = parentDropdownStyle.top + parentDropdownStyle.height + 2;

              menuElement.style.transform = `translate3d(${menuElementStyle.left}px, ${calculatedTop}px, 0px)`;
              menuElement.style.opacity = 1;
            }
            menuElement.style.opacity = 1;
          }
        }
      }, 15);
    }
  }, [isOpen]);

  useEffect(() => {
    const currentDropdown = document.querySelectorAll(".custom-pricing-dropdown.show")[0];
    if (currentDropdown) {
      const hideItemDropdownOnScroll = e => {
        if (isOpen) toggle(e);
      };
      document.addEventListener("scroll", hideItemDropdownOnScroll, true);
      return () => {
        document.removeEventListener("scroll", hideItemDropdownOnScroll, true);
      };
    }

    return () => {
      setShowFrequencyUpdateField(false);
    };
  }, [isOpen]);

  return (
    <Dropdown ref={dropdownRef} isOpen={isOpen} toggle={toggle}>
      <DropdownToggle tag="a" className={cx(style["dropdown-toggle"], style["custom-tooltip"])}>
        <Icon icon="ui-more" />
        <div className={cx(style["custom-tooltip-content"], style["top"])}>More</div>
      </DropdownToggle>
      <DropdownMenu
        right
        ref={dropdownRef}
        className={cx(style["shadow"], style["border-0"], style["rounded-sm"], "custom-pricing-dropdown", {
          [customDropdownMenuClass]: customDropdownMenuClass,
        })}
        container={dropdownContainer ? dropdownContainer : undefined}
        style={dropdownContainer && { opacity: 0 }}>
        <div>
          <DropdownItem onClick={toggleBillingFrequincyOption} tag={"a"} className={cx(style["mb-1"], style["py-2"])}>
            {showFrequencyUpdateField && (
              <span className={cx(style["d-flex"], style["me-1"], style["ms-n1"])}>
                <Icon icon="ui-arrow-left" />
              </span>
            )}
            <h6 className={style["m-0"]}>Billing Frequency</h6>
            {!showFrequencyUpdateField && (
              <span className={cx(style["ms-auto"], style["d-flex"])}>
                <Icon icon="ui-arrow-right" />
              </span>
            )}
          </DropdownItem>
          {showFrequencyUpdateField && (
            <div className={cx(style["px-3"], style["frequency"])}>
              <Input
                cssModule={style}
                returnType="formGroup"
                type="dropdown"
                classNamePrefix="select"
                getOptionValue={option => option.name}
                getOptionLabel={option => <span>{option.name}</span>}
                options={billingFrequencyOptions ?? []}
                updateState={option => updateBillingFrequency(option)}
                defaultValue={defaultBillingFrequency}
              />
            </div>
          )}
        </div>
        {/* Below show/hide section */}
        {!showFrequencyUpdateField && (
          <div>
            <DropdownItem tag="span">
              <ToggleDropdownItem
                toggleId={`optionalItem-${sectionIdx}-${itemIdx}`}
                label={"Optional Item"}
                checked={
                  tableConfigData?.sections?.[sectionIdx]?.items[itemIdx]?.optionalItem ||
                  tableConfigData?.sections?.[sectionIdx]?.optionalItem
                }
                disabled={tableConfigData?.sections?.[sectionIdx]?.optionalItem}
                selectfn={() =>
                  updateTableConfig(
                    { optionalItem: !tableConfigData?.sections?.[sectionIdx]?.items[itemIdx]?.optionalItem },
                    "items",
                    sectionIdx,
                    itemIdx
                  )
                }
              />
            </DropdownItem>
            <DropdownItem tag="span">
              <ToggleDropdownItem
                toggleId={`editableQuantity-${sectionIdx}-${itemIdx}`}
                label={"Editable Quantity"}
                checked={tableConfigData?.sections?.[sectionIdx]?.items[itemIdx]?.editableQuantity}
                selectfn={() =>
                  updateTableConfig(
                    { editableQuantity: !tableConfigData?.sections?.[sectionIdx]?.items[itemIdx]?.editableQuantity },
                    "items",
                    sectionIdx,
                    itemIdx
                  )
                }
              />
              <div className={cx(style["ms-2"])}>
                <Icon icon="ui-info" additionalclass={"font-14"} id={`itemQtyTooltip-${sectionIdx}-${itemIdx}`} />
                <UncontrolledTooltip placement="bottom" target={`itemQtyTooltip-${sectionIdx}-${itemIdx}`}>
                  Allow recipient to edit <br></br>the quantity
                </UncontrolledTooltip>
              </div>
            </DropdownItem>
            {tableConfigData?.sections?.[sectionIdx]?.items[itemIdx]?.editableQuantity && (
              <DropdownItem tag={"span"} className={style["editable-quantity"]}>
                <ItemFields
                  label={"Min"}
                  type={"text"}
                  itemValue={itemInfo?.minQty}
                  slug={"minQty"}
                  sectionIdx={sectionIdx}
                  itemIdx={itemIdx}
                  updateSectionValue={updateSectionValue}
                />
                <ItemFields
                  label={"Max"}
                  type={"text"}
                  itemValue={itemInfo?.maxQty}
                  slug={"maxQty"}
                  sectionIdx={sectionIdx}
                  itemIdx={itemIdx}
                  updateSectionValue={updateSectionValue}
                />
              </DropdownItem>
            )}
            <DropdownItem divider />
            <DropdownItem tag="span" className={style["font-14"]}>
              <div
                className={cx(style["d-flex"], style["align-items-center"], {
                  [style["disable"]]: tableConfigData.sections[sectionIdx].items.length < 2,
                  [style["cursor-pointer"]]: tableConfigData.sections[sectionIdx].items.length > 1,
                })}
                onClick={() => deleteSectionItem("item", sectionIdx, itemIdx)}>
                <Icon icon="ui-trash" additionalclass={"me-2"} />
                <span>Delete Item</span>
              </div>
              {tableConfigData.sections[sectionIdx].items.length < 2 && (
                <div className={cx(style["ms-2"])}>
                  <Icon icon="ui-info" additionalclass={"font-14"} id={`itemTooltip-${sectionIdx}-${itemIdx}`} />
                  <UncontrolledTooltip placement="bottom" target={`itemTooltip-${sectionIdx}-${itemIdx}`}>
                    You have only one item in this <br></br> section. Hence it can’t be deleted
                  </UncontrolledTooltip>
                </div>
              )}
            </DropdownItem>
          </div>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

const SectionDropdown = ({ tableConfigData, updateTableConfig, sectionIdx, deleteSectionItem }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Toggle the dropdown
  const toggle = e => {
    if (e.target.closest(".custom-pricing-dropdown")) setIsOpen(true);
    else setIsOpen(prevState => !prevState);
  };

  return (
    <Dropdown isOpen={isOpen} toggle={toggle}>
      <DropdownToggle tag="a" className={cx(style["dropdown-toggle"], style["custom-tooltip"])}>
        <Icon icon="ui-more" />
        <div className={cx(style["custom-tooltip-content"], style["top"])}>More</div>
      </DropdownToggle>
      <DropdownMenu
        right
        className={cx(style["shadow"], style["border-0"], style["rounded-sm"], "custom-pricing-dropdown")}>
        <DropdownItem tag="span">
          <ToggleDropdownItem
            toggleId={`optionalSection-${sectionIdx}`}
            label={"Optional Section"}
            checked={tableConfigData?.sections[sectionIdx].optionalSection || tableConfigData?.optionalSection}
            disabled={tableConfigData?.optionalSection}
            selectfn={() =>
              updateTableConfig(
                { optionalSection: !tableConfigData.sections[sectionIdx].optionalSection },
                "sections",
                sectionIdx
              )
            }
          />
        </DropdownItem>
        <DropdownItem tag="span">
          <ToggleDropdownItem
            toggleId={`optionalItem-${sectionIdx}`}
            label={"Optional Item"}
            checked={tableConfigData?.sections?.[sectionIdx]?.optionalItem}
            selectfn={() =>
              updateTableConfig(
                { optionalItem: !tableConfigData.sections[sectionIdx].optionalItem },
                "sections",
                sectionIdx
              )
            }
          />
        </DropdownItem>
        {tableConfigData?.sections?.[sectionIdx]?.optionalItem && (
          <>
            <DropdownItem tag="span">
              <Input
                type="radio"
                id={`singleOpctionDropdown${sectionIdx}`}
                radioClass="radio-icon mb-0"
                text="Single Selection"
                checked={"single" == tableConfigData.sections[sectionIdx]?.itemSelectionType}
                className={style["form-check-input"]}
                onChange={() => updateTableConfig({ itemSelectionType: "single" }, "sections", sectionIdx)}
              />
            </DropdownItem>
            <DropdownItem tag="span">
              <Input
                type="radio"
                id={`multipleOpctionDropdown${sectionIdx}`}
                radioClass="radio-icon  mb-0"
                text="Multiple Selection"
                checked={"multiple" == tableConfigData.sections[sectionIdx]?.itemSelectionType}
                className={style["form-check-input"]}
                onChange={() => updateTableConfig({ itemSelectionType: "multiple" }, "sections", sectionIdx)}
              />
            </DropdownItem>
          </>
        )}
        <DropdownItem divider />
        <DropdownItem tag="span" className={cx(style["font-14"])}>
          <div
            className={cx(style["d-flex"], style["align-items-center"], {
              [style["disable"]]: tableConfigData.sections.length < 2,
              [style["cursor-pointer"]]: tableConfigData.sections.length > 1,
            })}
            onClick={() => deleteSectionItem("section", sectionIdx)}>
            <Icon icon="ui-trash" additionalclass={"me-2"} />
            <span>Delete Section</span>
          </div>
          {tableConfigData.sections.length < 2 && (
            <div className={cx(style["ms-2"])}>
              <Icon icon="ui-info" additionalclass={"font-14"} id={`sectionTooltip-${sectionIdx}`} />
              <UncontrolledTooltip placement="bottom" target={`sectionTooltip-${sectionIdx}`}>
                You have only one section in this <br></br> pricing table. Hence it can’t be <br></br> deleted
              </UncontrolledTooltip>
            </div>
          )}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

const ItemImageUpload = ({
  itemInfo,
  enableUpload = false,
  documentId,
  clusterCode,
  dropdownContainer,
  setCustomMsg,
  sectionIdx,
  itemIdx,
  updateSectionValue,
}) => {
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const [itemImages, setItemImages] = useState([]);
  const imageCountRef = useRef(0);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = e => {
    setDropdownOpen(prevState => !prevState);
  };

  const uploadItemImages = e => {
    if (e?.target?.parentNode?.id === "close-image-upload") {
      setDropdownOpen(false);
      return;
    }
    setDropdownOpen(true);
  };

  const onChangeFn = (updateValue, hashkey = null) => {
    imageCountRef.current = itemImages.length + updateValue.length;
    const formData = new FormData();

    let uploadedImageData = [];
    let itemUrls = [];

    updateValue.forEach(fileData => {
      formData.append("files", fileData);
      uploadedImageData.push({
        id: `${fileData.lastModified}${generateUniqueHash()}`,
        name: fileData.name,
      });
    });

    dispatch(uploadPricingItemAsset(documentId, clusterCode, formData)).then(resp => {
      resp?.result?.file_urls.forEach((item, itemIdx) => {
        uploadedImageData[itemIdx].url = item[itemIdx];
        itemUrls.push(item[itemIdx]);
      });
      setItemImages([...itemImages, ...uploadedImageData]);

      if (updateSectionValue)
        updateSectionValue({ images: [...itemInfo.data.images, ...itemUrls] }, sectionIdx, itemIdx);
    });
  };

  const deleteById = (array, idToDelete) => {
    return array.filter(item => item.id !== idToDelete);
  };

  const onDeleteFn = assetId => {
    if (!assetId) return;
    // imageCountRef.current = itemImages.length - 1;
    let updatedImageArray = deleteById(itemImages, assetId);
    let itemUrls = [];

    updatedImageArray.forEach(item => {
      itemUrls.push(item.url);
    });

    updateSectionValue({ images: [...itemUrls] }, sectionIdx, itemIdx);
  };

  const closeModal = () => {
    setTimeout(() => {
      setDropdownOpen(false);
    }, 20);
  };

  const dropdownWidthCalculation = itemCount => {
    let expectedWidth = itemCount < 1 ? 142 : itemCount < 6 ? 142 + 128 * itemCount : 142 + 128 * 5;
    return expectedWidth;
  };

  useEffect(() => {
    if (itemInfo?.data?.images) {
      let imageArray = [];
      itemInfo.data.images.forEach(imageUrl => {
        imageArray.push({
          id: `${generateUniqueHash()}`,
          name: imageUrl.split("/").pop(),
          url: imageUrl,
        });
      });
      setItemImages(imageArray);
    }
  }, [itemInfo?.data?.images]);

  // Reset image upload dropdown width based on image count
  useEffect(() => {
    setTimeout(() => {
      imageCountRef.current = itemImages.length;
      let dropdownMenuElement = dropdownRef.current?.menuRef?.current;
      if (dropdownMenuElement) {
        const dropdownWidth = parseInt(dropdownMenuElement.style.maxWidth, 10);
        let expectedWidth = dropdownWidthCalculation(imageCountRef.current);
        if (dropdownWidth > expectedWidth) {
          dropdownMenuElement.style.maxWidth = `${expectedWidth}px`;
        }
      }
    }, 150);
  }, [itemImages]);

  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => {
        const currentDropdown = document.querySelectorAll(".item-image-option-dropdown.show")[0];

        if (currentDropdown) {
          // Check if menu has placed top of the dropdown and container present
          if (dropdownRef?.current && dropdownRef.current?.menuRef?.current && dropdownContainer) {
            const parentDropdown = dropdownRef.current.containerRef.current;
            const menuElement = dropdownRef.current.menuRef.current;

            const parentDropdownStyle = parentDropdown.getBoundingClientRect();
            const menuElementStyle = menuElement.getBoundingClientRect();
            if (menuElementStyle?.top < parentDropdownStyle?.top) {
              let calculatedTop = parentDropdownStyle.top + parentDropdownStyle.height + 2;

              menuElement.style.transform = `translate3d(${menuElementStyle.left}px, ${calculatedTop}px, 0px)`;
              menuElement.style.opacity = 1;
            }
            menuElement.style.opacity = 1;
          }
        }
      }, 15);
    }
  }, [dropdownOpen]);
  return (
    <div className={style["item-image-dropdown"]} onClick={() => (enableUpload ? uploadItemImages() : "")}>
      <Dropdown ref={dropdownRef} className={style[""]} isOpen={dropdownOpen} toggle={toggle}>
        <DropdownToggle tag={"a"} className={cx(style["w-auto"], style["p-0"], style["min-width-none"])}>
          <figure
            className={cx(style["item-thumbnail"], {
              [style["product-item-image"]]: itemInfo?.data?.images?.length > 0,
            })}>
            {itemInfo?.data?.images?.length > 0 && (
              <img src={itemInfo.data.images[0]} className={cx(style["img-fluid"])} />
            )}
            {(!itemInfo?.data?.images || itemInfo?.data?.images?.length === 0) && <Icon icon="ui-pictures" />}
          </figure>

          {enableUpload && (
            <span className={cx(style["count"], { [style["font-18"]]: itemInfo?.data?.images?.length > 1 })}>
              +{itemInfo?.data?.images?.length > 1 && itemInfo?.data?.images?.length - 1}
            </span>
          )}
        </DropdownToggle>
        <DropdownMenu
          ref={dropdownRef}
          className={cx(style["shadow"], style["border-0"], style["PT-imageUpload"], "item-image-option-dropdown")}
          container={dropdownContainer ? dropdownContainer : undefined}
          style={{
            ...(dropdownContainer && { opacity: 0 }),
            ...{ maxWidth: `${dropdownWidthCalculation(imageCountRef.current)}px` },
          }}>
          <div
            className={cx(
              style["d-flex"],
              style["justify-content-between"],
              style["align-items-center"],
              style["title"]
            )}>
            <h6 className={cx(style["mb-0"], style["fw-7"], style["align-items-center"], style["d-flex"])}>
              Images
              <div className={cx(style["custom-tooltip"], style["ms-1"])}>
                <Icon icon="information" additionalclass={"font-13"} />
                <div className={cx(style["custom-tooltip-content"], style["bottom"])}>
                  You can add up to 6 images in JPG <br /> or PNG format at 5 MB each
                </div>
              </div>
            </h6>
            <div className={cx(style["rounded"], style["cross-modal"])} id={"close-image-upload"} onClick={closeModal}>
              <Icon icon="ui-close" />
            </div>
          </div>
          <AssetField
            type={"image"}
            acceptType={"image/jpeg, image/png"}
            maxSize={{ image: 5 }}
            defaultAssets={itemImages || []}
            currentAssetList={itemImages || []}
            updateFunction={onChangeFn}
            deleteAssetFn={onDeleteFn}
            maxCount={6}
            setCustomMsg={setCustomMsg}
            emptyAssetList={true}
          />
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

const SectionComponent = ({
  sectionInfo,
  sectionIdx,
  tableConfigData,
  handleBlur,
  isEditable,
  handleEditClick,
  changeSectionName,
  onKeyDownFn,
  formattedCurrency,
  moveSection,
  updateTableConfig,
  deleteSectionItem,
  updateSectionItem,
  currentCurrency,
  updateSectionValue,
  calculatedSections,
  billingSuffixes,
  addSectionItem,
  itemList,
  availableTaxes,
  setTableConfigData,
  dropdownContainer,
  modalFullScreen,
  isLoadingItem,
  isDescriptionFocused,
  setIsDescriptionFocused,
  usageUnitData,
  setCustomMsg,
}) => {
  const [sectionItems, setSectionItems] = useState(sectionInfo.items);
  const [readyToUpdate, setReadyToUpdate] = useState(false);
  const [sectionActualWidth, setSectionActualWidth] = useState(0);
  const [insertWidth, setInsertWidth] = useState(false);
  const [itemNameDropdown, setItemNameDropdown] = useState(false);
  const [tooltipId, setTooltipId] = useState();
  const [itemTooltipValue, setItemTooltipValue] = useState();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { id: documentId } = useSelector(state => state.document?.documentDetails?.data);
  const companyInfo = useSelector(state => state?.auth?.company);

  const sectionRef = useRef(null);

  const { items: dragItems, containerProps, getItemProps } = useDragAndDrop(sectionItems, "vertical", setReadyToUpdate);

  const updateSelectionType = type => {
    let updatedTable = { ...tableConfigData };
    if (type === "single") {
      updatedTable.sections.forEach((section, sectionIndex) => (section.active = sectionIndex === sectionIdx));
    } else {
      updatedTable.sections[sectionIdx].active = !updatedTable.sections[sectionIdx].active;
    }
    setTableConfigData(updatedTable);
  };

  const updateItemSelectionType = (type, itemIdx) => {
    let updatedTable = { ...tableConfigData };
    if (type === "single") {
      updatedTable.sections[sectionIdx]?.items.forEach((item, itemIndex) => (item.active = itemIndex === itemIdx));
    } else {
      updatedTable.sections[sectionIdx].items[itemIdx].active =
        !updatedTable.sections[sectionIdx].items[itemIdx].active;
    }
    setTableConfigData(updatedTable);
  };

  const handleMouseEnter = (e, id) => {
    if (e.target) {
      setTooltipId(`item-name-${id}`);
      setItemTooltipValue(e.target.innerText);
      setItemNameDropdown(e.target.scrollWidth > e.target.clientWidth);
    }
  };

  const handleMouseLeave = () => {
    setTooltipId();
    setItemTooltipValue();
    setItemNameDropdown();
  };

  const toggle = e => {
    setDropdownOpen(prevState => !prevState);
  };

  useEffect(() => {
    if (!readyToUpdate) {
      setReadyToUpdate(false);
      return;
    }
    let updatedTable = { ...tableConfigData };
    updatedTable.sections[sectionIdx].items = dragItems;
    setTableConfigData(updatedTable);
    setReadyToUpdate(false);
  }, [dragItems]);

  useEffect(() => {
    if (sectionInfo?.items) setSectionItems(sectionInfo.items);
  }, [sectionInfo?.items]);

  // Below code to set item details width
  useLayoutEffect(() => {
    const updateWidth = () => {
      setInsertWidth(false);
      setTimeout(() => {
        if (sectionRef.current) {
          let currScrollWidth = sectionRef.current.scrollWidth;
          if (sectionRef?.current?.getBoundingClientRect()?.width > currScrollWidth) {
            currScrollWidth = sectionRef.current.getBoundingClientRect().width;
          }

          setSectionActualWidth(currScrollWidth);
          setInsertWidth(true);
        }
      }, 100);
    };

    updateWidth(); // Initial measurement

    // Optional: Re-measure on window resize (if needed)
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [modalFullScreen]);

  return (
    <Card className={cx(style["mb-3"])} key={sectionIdx}>
      <CardHeader className={cx(style["d-flex"], style["justify-content-between"], style["align-items-center"])}>
        <div className={cx(style["d-flex"], style["align-items-center"], style["ps-2"])}>
          <div className={style["section-name-space"]}>
            {(tableConfigData?.optionalSection || sectionInfo?.optionalSection) && (
              <>
                {(tableConfigData?.sectionSelectionType === "multiple" || sectionInfo?.optionalSection) && (
                  <div className={cx(style["custom-control"], style["custom-checkbox"], style["form-check"])}>
                    <Input
                      cssModule={style}
                      returnType="noGroup"
                      type="checkbox"
                      id={`multipleOpction${sectionIdx}`}
                      checked={sectionInfo?.active}
                      className={style["form-check-input"]}
                      onChange={() => updateSelectionType("multiple")}
                    />
                  </div>
                )}
                {tableConfigData?.sectionSelectionType === "single" && !sectionInfo?.optionalSection && (
                  <Input
                    type="radio"
                    id={`singleOpction${sectionIdx}`}
                    name="sectional-selection"
                    radioClass="radio-icon mb-0"
                    checked={sectionInfo?.active}
                    className={style["form-check-input"]}
                    onChange={() => updateSelectionType("single")}
                  />
                )}
              </>
            )}
          </div>
          <h6 className={cx(style["mb-0"], style["me-5"], style["fw-7"], style["edit-title"], style["d-flex"])}>
            <div
              id={`section-Name-${sectionIdx}`}
              contentEditable={isEditable}
              onBlur={e => handleBlur(e, sectionIdx)}
              onInput={e => changeSectionName(e)}
              onKeyDown={e => onKeyDownFn(e)}
              suppressContentEditableWarning={true}
              className={cx({ [style["rename"]]: isEditable })}>
              {sectionInfo.name}
            </div>
            {!isEditable && (
              <a onClick={() => handleEditClick(sectionIdx)}>
                <Icon icon="ui-edit" />
              </a>
            )}
          </h6>
        </div>
        <ul className={cx(style["d-flex"], style["align-items-center"])}>
          <li className={cx({ [style["disable"]]: !sectionInfo?.active })}>
            <h6 className={cx(style["mb-0"], style["fw-7"])}>Total</h6>
          </li>
          <li className={cx(style["ms-2"], { [style["disable"]]: !sectionInfo?.active })}>
            <h6 className={cx(style["mb-0"], style["fw-7"])}>
              <div>{formattedCurrency(calculatedSections[sectionIdx]?.total)}</div>
            </h6>
          </li>
          <li
            className={cx(style["custom-tooltip"], style["ms-3"], style["d-flex"], style["cursor-pointer"], {
              [style["disable"]]: sectionIdx === 0,
            })}
            onClick={() => moveSection(sectionIdx, "up")}>
            <Icon icon="ui-arrow-up2" />
            <div className={cx(style["custom-tooltip-content"], style["top"])}>Move Up</div>
          </li>
          <li
            className={cx(style["custom-tooltip"], style["ms-1"], style["d-flex"], style["cursor-pointer"], {
              [style["disable"]]: sectionIdx === tableConfigData.sections.length - 1,
            })}
            onClick={() => moveSection(sectionIdx, "down")}>
            <Icon icon="ui-arrow-down2" />
            <div className={cx(style["custom-tooltip-content"], style["top"])}>Move Down</div>
          </li>
          <li className={cx(style["more-action"], style["ms-3"])}>
            <SectionDropdown
              tableConfigData={tableConfigData}
              updateTableConfig={updateTableConfig}
              sectionIdx={sectionIdx}
              deleteSectionItem={deleteSectionItem}
            />
          </li>
        </ul>
      </CardHeader>
      <CardBody
        className={cx(style["card-space"], {
          [style["hide-discount"]]: !tableConfigData?.lineItemDiscount,
        })}>
        <div ref={sectionRef} className={cx(style["customScroll"], style["scroll-X"])}>
          <div className={cx(style["item-label"])} style={insertWidth ? { width: `${sectionActualWidth}px` } : {}}>
            <ul className={cx(style["d-flex"])}>
              <li>&nbsp;</li>
              {tableConfigData.columns?.map((item, idx) => {
                if (item.slug === "discount" && !tableConfigData.lineItemDiscount) return null;
                return (
                  <li key={idx} className={cx(item.className)}>
                    {item.name}
                  </li>
                );
              })}
            </ul>
          </div>
          <div {...containerProps}>
            {dragItems.map((itemInfo, itemIdx) => (
              <div
                className={cx(style["item-details"])}
                key={itemIdx}
                {...getItemProps(itemIdx)}
                style={insertWidth ? { width: `${sectionActualWidth}px` } : {}}>
                <div
                  className={cx(style["item-info"], { [style["hide-image-hover"]]: !itemInfo?.data?.["item_name"] })}>
                  {dragItems.length > 1 && <Icon icon="ui-drag2" additionalclass={"drag-icon"} />}
                  <ul className={cx(style["d-flex"], style["mb-2"], style["align-items-start"])}>
                    <li className={cx(style["ms-0"], style["me-n2"])}>
                      {(sectionInfo?.optionalItem || itemInfo?.optionalItem) && (
                        <>
                          {(sectionInfo?.itemSelectionType === "multiple" ||
                            (itemInfo.optionalItem && itemInfo.itemSelectionType === "multiple")) && (
                            <div className={cx(style["custom-control"], style["custom-checkbox"], style["form-check"])}>
                              <Input
                                cssModule={style}
                                returnType="noGroup"
                                type="checkbox"
                                id={`multipleOpction${sectionIdx}${itemIdx}`}
                                checked={itemInfo?.active}
                                className={style["form-check-input"]}
                                onChange={() => updateItemSelectionType("multiple", itemIdx)}
                              />
                            </div>
                          )}
                          {sectionInfo?.itemSelectionType === "single" && (
                            <Input
                              type="radio"
                              id={`singleOpction${sectionIdx}${itemIdx}`}
                              radioClass="radio-icon mb-0"
                              checked={itemInfo?.active}
                              className={style["form-check-input"]}
                              onChange={() => updateItemSelectionType("single", itemIdx)}
                            />
                          )}
                        </>
                      )}
                    </li>
                    <li className={cx(style["item-image"])}>
                      {/* <figure
                        className={cx(style["item-thumbnail"], {
                          [style["product-item-image"]]: itemInfo?.data?.images?.length > 0,
                        })}>
                        {itemInfo?.data?.images?.length > 0 && (
                          <img src={itemInfo.data.images[0]} className={cx(style["img-fluid"])} />
                        )}
                        {(!itemInfo?.data?.images || itemInfo?.data?.images?.length === 0) && (
                          <Icon icon="ui-pictures" />
                        )}
                      </figure>
                      {itemInfo?.data?.images?.length > 1 && (
                        <span className={style["count"]}>+{itemInfo?.data?.images?.length - 1}</span>
                      )} */}
                      <ItemImageUpload
                        dropdownOpen={dropdownOpen}
                        toggle={toggle}
                        itemInfo={itemInfo}
                        // onChangeFn={onChangeFn}
                        // onDeleteFn={onDeleteFn}
                        documentId={documentId}
                        clusterCode={companyInfo.cluster_code}
                        dropdownContainer={dropdownContainer}
                        enableUpload={!!itemInfo?.data?.["item_name"]}
                        setCustomMsg={setCustomMsg}
                        sectionIdx={sectionIdx}
                        itemIdx={itemIdx}
                        updateSectionValue={updateSectionValue}
                      />
                    </li>
                    {tableConfigData.columns?.map((item, idx) => {
                      if (item.slug === "discount" && !tableConfigData.lineItemDiscount) return null;

                      return (
                        <>
                          {item.slug === "item_name" && (
                            <>
                              <li key={idx} className={cx(item?.inputFieldClass)}>
                                <Input
                                  onMouseDown={e => e.stopPropagation()} // Prevent drag when clicking input
                                  cssModule={style}
                                  returnType="formGroup"
                                  type="dropdown"
                                  required={true}
                                  classNamePrefix="select"
                                  dropDownMenuClass="text-capitalize"
                                  className="input-with-link"
                                  isOptionSelected={option => option.name === itemInfo?.data?.item_name?.name}
                                  getOptionValue={option => [option?.name, option?.code, option?.sku]}
                                  getOptionLabel={option => (
                                    <span
                                      id={`item-name-${option._id}`}
                                      onMouseEnter={e => handleMouseEnter(e, option._id)}
                                      onMouseLeave={handleMouseLeave}
                                      onMouseDown={handleMouseLeave}>
                                      {option?.name}
                                    </span>
                                  )}
                                  options={itemList}
                                  defaultValue={itemInfo?.data?.item_name || {}}
                                  updateState={e => updateSectionItem(sectionIdx, itemIdx, e)}
                                  showCustomInputOption={true}
                                  container={dropdownContainer}
                                  customDropdownMenuClass={`item-name-dropdownmenu ${
                                    !tableConfigData?.lineItemDiscount ? "discount-hide" : ""
                                  }`}
                                  isLoading={isLoadingItem}
                                  closeOnScroll={true}
                                />
                              </li>
                              {/* Below tooltip for option full value shows if ellipsis option label */}
                              {/* {tooltipId && (
                                <Tooltip isOpen={itemNameDropdown} target={tooltipId} placement={"bottom-end"}>
                                  {itemTooltipValue}
                                </Tooltip>
                              )} */}
                            </>
                          )}

                          {item.slug === "unit_price" && (
                            <li
                              key={idx}
                              className={cx(item?.inputFieldClass, style["d-flex"], style["align-items-center"])}>
                              <ItemFields
                                onMouseDown={e => e.stopPropagation()}
                                fieldType="group_addon_field"
                                itemValue={itemInfo?.data?.[item.slug]}
                                slug={item.slug}
                                sectionIdx={sectionIdx}
                                itemIdx={itemIdx}
                                updateSectionValue={updateSectionValue}
                                isEnable={!!itemInfo?.data?.["item_name"]}
                                leftAddonEnabled={true}
                                leftAddonData={currentCurrency?.symbol}
                                rightAddonEnabled={!!itemInfo?.data?.["item_name"]}
                                rightAddonData={
                                  itemInfo?.data?.["item_name"]
                                    ? normalizeUsageUnitForUI(itemInfo?.data?.usage_unit, usageUnitData)
                                    : null
                                }
                                maxLength={12}
                              />
                            </li>
                          )}

                          {item.slug === "quantity" && (
                            <li
                              key={idx}
                              className={cx(item?.inputFieldClass, style["d-flex"], style["align-items-center"])}>
                              <ItemFields
                                itemValue={itemInfo?.data?.[item.slug]}
                                fieldType={"spinner"}
                                slug={item.slug}
                                spinnerClass="input-spinner"
                                sectionIdx={sectionIdx}
                                itemIdx={itemIdx}
                                updateSectionValue={updateSectionValue}
                                isEnable={!!itemInfo?.data?.["item_name"]}
                                showAddonButton={itemInfo?.editableQuantity || false}
                                onDecrease={() =>
                                  updateSectionValue(
                                    {
                                      quantity:
                                        parseFloat(itemInfo?.data?.[item.slug]) - 1 < itemInfo?.minQty
                                          ? itemInfo?.minQty
                                          : parseFloat(itemInfo?.data?.[item.slug]) - 1,
                                    },
                                    sectionIdx,
                                    itemIdx
                                  )
                                }
                                onIncrease={() =>
                                  updateSectionValue(
                                    {
                                      quantity:
                                        parseFloat(itemInfo?.data?.[item.slug]) + 1 > itemInfo?.maxQty
                                          ? itemInfo?.maxQty
                                          : parseFloat(itemInfo?.data?.[item.slug]) + 1,
                                    },
                                    sectionIdx,
                                    itemIdx
                                  )
                                }
                                increasedisable={
                                  itemInfo?.maxQty <= itemInfo?.data?.[item.slug] || !itemInfo?.data?.["item_name"]
                                }
                                decreasedisable={
                                  itemInfo?.minQty >= itemInfo?.data?.[item.slug] || !itemInfo?.data?.["item_name"]
                                }
                                maxLength={6}
                                maxMinLimt={
                                  itemInfo?.editableQuantity
                                    ? { maxQty: itemInfo.maxQty, minQty: itemInfo.minQty }
                                    : undefined
                                }
                              />
                            </li>
                          )}

                          {item.slug === "discount" && (
                            <li
                              key={idx}
                              className={cx(item?.inputFieldClass, style["d-flex"], style["align-items-center"])}>
                              <ItemFields
                                fieldType="discount"
                                itemValue={itemInfo?.data?.[item.slug]}
                                slug={item.slug}
                                sectionIdx={sectionIdx}
                                itemIdx={itemIdx}
                                updateSectionValue={updateSectionValue}
                                isEnable={!!itemInfo?.data?.["item_name"]}
                                discountOptions={[currentCurrency?.symbol]}
                                defaultDiscountType={itemInfo?.data?.discountType}
                                maxLength={7}
                                dropdownContainer={dropdownContainer}
                                customDropdownMenuClass={"discount-dropdownmenu"}
                                currData={itemInfo?.data}
                                closeOnScroll={true}
                              />
                            </li>
                          )}

                          {item.slug === "tax" && (
                            <li key={idx} className={cx(item?.inputFieldClass)}>
                              <TaxInputDropdown
                                additionalclass="no-drag"
                                disabled={!!!itemInfo?.data?.["item_name"]}
                                options={availableTaxes ?? []}
                                updateState={e => updateSectionValue({ tax: e }, sectionIdx, itemIdx)}
                                defaultValue={itemInfo?.data?.tax || []}
                                dropdownContainer={dropdownContainer}
                                customDropdownMenuClass={`tax-dropdownmenu ${
                                  !tableConfigData?.lineItemDiscount ? "discount-hide" : ""
                                }`}
                                showManageOption={false}
                                closeOnScroll={true}
                              />
                            </li>
                          )}

                          {item.slug === "amount" && (
                            <li
                              key={idx}
                              className={cx(item?.inputFieldClass, { [style["disable"]]: !itemInfo?.active })}>
                              <div className={style["amount-content"]}>
                                <div className={style["amount-no"]}>
                                  {formattedCurrency(calculatedSections[sectionIdx]?.items[itemIdx]?.amount)}
                                </div>
                                {itemInfo?.data?.["item_name"] && (
                                  <div className={cx(style["font-13"], style["text-muted"], style["text-right"])}>
                                    {billingSuffixes?.[itemInfo?.data?.billing_frequency]}
                                  </div>
                                )}
                              </div>
                            </li>
                          )}
                        </>
                      );
                    })}

                    <li className={cx(style["more-action"])}>
                      <ItemDropdown
                        tableConfigData={tableConfigData}
                        updateTableConfig={updateTableConfig}
                        sectionIdx={sectionIdx}
                        itemIdx={itemIdx}
                        itemInfo={itemInfo}
                        deleteSectionItem={deleteSectionItem}
                        updateSectionValue={updateSectionValue}
                        dropdownContainer={dropdownContainer}
                        customDropdownMenuClass={"more-option-dropdownmenu"}
                      />
                    </li>
                  </ul>
                </div>
                {tableConfigData?.showItemCode && itemInfo?.data?.item_code && (
                  <div className={cx(style["item-code"], style["ms-2"], style["mb-2"], style["d-block"])}>
                    <span className={style["fw-6"]}>Item Code:</span> {itemInfo?.data?.item_code}
                  </div>
                )}
                {itemInfo?.data?.["item_name"] && (
                  <div className={cx(style["item-description"], style["ms-2"], style["d-block"])}>
                    <div className="form-group">
                      <ItemFields
                        fieldType={"textarea"}
                        itemValue={itemInfo?.data?.description}
                        slug={"description"}
                        sectionIdx={sectionIdx}
                        itemIdx={itemIdx}
                        updateSectionValue={updateSectionValue}
                        placeholder="Description (Max 300 characters)"
                        maxLength={300}
                        addSectionItem={addSectionItem}
                        setIsDescriptionFocused={setIsDescriptionFocused}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className={cx(style["add-item"])}>
          <div className={cx(style["d-flex"], style["align-items-center"])}>
            <a
              className={cx(style["d-flex"], style["align-items-center"], style["color-33"])}
              tabIndex={0}
              onClick={() => {
                if (isDescriptionFocused) return;
                addSectionItem("item", sectionIdx);
              }}
              data-role="add-item">
              <Icon icon="ui-plus" additionalclass={"me-2"} />
              <span>Item</span>
            </a>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

const PricingTable = ({ closeModal, modalFullScreen, setModalFullScreen, editTable }) => {
  let { metadata, widgets, updateWidgets } = useContext(EditorContext);

  const dispatch = useDispatch();
  const dropdownContainer = document.querySelector(".pricing-table-modal");

  const { id: companyId } = useSelector(state => state.auth?.user?.company);
  const { id: documentId } = useSelector(state => state.document?.documentDetails?.data);
  const { proposalModuleListInfo } = useSelector(state => state.company);

  const tableDefaultData = JSON.parse(JSON.stringify(widgetConfig?.pricing_tables));

  const {
    state: tableConfigData,
    updateState: setTableConfigData,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo(editTable ? editTable : tableDefaultData?.tableConfig);

  const [isOpen, setIsOpen] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [currencyList, setCurrencyList] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState({});
  const [currentCurrency, setCurrentCurrency] = useState("");
  const [availableTaxes, setAvailableTaxes] = useState([]);
  const [discountOptions, setDiscountOptions] = useState({
    lineItemDiscount: editTable ? editTable.lineItemDiscount : true,
    oneLineDiscount: editTable ? editTable.oneLineDiscount : false,
    oneLineDiscountValue: editTable
      ? editTable.oneLineDiscountValue
      : (tableDefaultData?.tableConfig?.oneLineDiscountValue ?? 0),
    oneLineDiscountType: editTable
      ? editTable.oneLineDiscountType
      : (tableDefaultData?.tableConfig?.oneLineDiscountType ?? "%"),
  });

  const [insertHtmlMode, setInsertHtmlMode] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [insertedHtmlData, setInsertedHtmlData] = useState([]);
  const [addWidgetClicked, setAddWidgetClicked] = useState(false);
  const [dataparam, setDataParam] = useState();
  const [assetInnerContent, setAssetInnerContent] = useState();
  const [widgetWidth, setWidgetWidth] = useState();
  const [widgetHeight, setWidgetHeight] = useState();
  const [customCss, setCustomCss] = useState(false);
  const [disableInsert, setDisableInsert] = useState(true);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  const [disableCurrencyUpdate, setDisableCurrencyUpdate] = useState(false);
  const [isLoadingItem, setIsLoadingItem] = useState(false);
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
  const [customMsg, setCustomMsg] = useState();
  const htmlMode = false;

  const moduleList = ["items", "currency", "taxes", "usage_units"];

  const {
    sections: calculatedSections,
    grandSubtotal,
    oneLineDiscountAmount,
    detailedTaxes,
    grandTotal,
  } = useProposalCalculator(tableConfigData.sections, discountOptions);

  const { postion: getPosition } = useAlignment(addWidgetClicked, widgetWidth, widgetHeight, "middle-center");
  const getNewWidgetObject = useAddWidget(
    addWidgetClicked,
    PRICING_TABLE,
    dataparam,
    getPosition,
    widgetWidth,
    widgetHeight
  );
  useElementInnerHtml(
    addWidgetClicked,
    setAddWidgetClicked,
    PRICING_TABLE,
    assetInnerContent,
    dataparam,
    getNewWidgetObject,
    customCss
  );

  const setCurrencyInfo = currData => {
    let defaultSelectedCurrency;
    if (editTable) {
      let filteredCurrency = currData.find(currency => currency.name === tableConfigData?.currency);
      if (filteredCurrency) defaultSelectedCurrency = filteredCurrency;
      else defaultSelectedCurrency = currData.find(currency => currency.default);
    } else defaultSelectedCurrency = currData.find(currency => currency.default);
    let enabledCurrency = currData.filter(currency => currency.enabled);
    setCurrencyList(enabledCurrency);
    setDefaultCurrency({ name: defaultSelectedCurrency?.name ?? currData[0].name });
    setCurrentCurrency(defaultSelectedCurrency ?? currData[0]);
  };

  const setTaxesInfo = taxList => {
    const transformedArray = taxList.map(item => ({
      name: `${item.name} ${item.rate}%`,
      value: `${item.name}_${item.rate}%`,
      id: item._id,
    }));
    setAvailableTaxes(transformedArray);
  };

  const fetchRecords = (moduleName, payload) => {
    if (moduleName === "currency") {
      if (proposalModuleListInfo?.currency?.data) {
        setCurrencyInfo(proposalModuleListInfo.currency.data);
      } else {
        dispatch(fetchProposalModuleList(moduleName, payload)).then(resp => {
          setCurrencyInfo(resp.results.data);
        });
      }
    } else if (moduleName === "taxes") {
      if (proposalModuleListInfo?.taxes?.data) {
        setTaxesInfo(proposalModuleListInfo.taxes.data);
      } else {
        dispatch(fetchProposalModuleList(moduleName, payload)).then(resp => {
          setTaxesInfo(resp.results.data);
        });
      }
    } else if (moduleName === "usage_units") {
      if (!proposalModuleListInfo?.usage_units?.data) {
        dispatch(fetchProposalModuleList(moduleName, payload));
      }
    } else {
      setIsLoadingItem(true);
      dispatch(fetchProposalItems()).then(resp => {
        setItemList(resp.data.data);
        setIsLoadingItem(false);
      });
    }
  };

  const setFullScreen = () => {
    setModalFullScreen(!modalFullScreen);
  };

  const close = () => {
    closeModal && closeModal();
    if (modalFullScreen) setModalFullScreen(false);
  };

  // Toggle the dropdown
  const toggle = e => {
    if (e.target.closest(".custom-pricing-dropdown")) setIsOpen(true);
    else setIsOpen(prevState => !prevState);
  };

  const getModalHeight = () => {
    if (window.innerWidth < 480) return "calc(95vh - 3rem)";
    if (window.innerWidth < 768) return "calc(90vh - 5rem)";
    return "calc(84vh - 10rem)";
  };

  // Update Table config data
  const updateTableConfig = (keyValue, type = null, sectionIdx = null, itemIdx = null) => {
    let updatedTable = { ...tableConfigData };
    const keyName = Object.keys(keyValue)[0];
    const keyData = keyValue[keyName];
    if (type === "sections") {
      updatedTable.sections[sectionIdx] = { ...updatedTable.sections[sectionIdx], ...keyValue };
    } else if (type === "items") {
      updatedTable.sections[sectionIdx].items[itemIdx] = {
        ...updatedTable.sections[sectionIdx].items[itemIdx],
        ...keyValue,
      };
    } else {
      updatedTable = { ...updatedTable, ...keyValue };

      const discountKeys = ["oneLineDiscount", "lineItemDiscount", "oneLineDiscountType", "oneLineDiscountValue"];
      const discountUpdates = Object.keys(keyValue)
        .filter(key => discountKeys.includes(key))
        .reduce((obj, key) => ({ ...obj, [key]: keyValue[key] }), {});

      if (Object.keys(discountUpdates).length > 0) {
        setDiscountOptions({ ...discountOptions, ...discountUpdates });
      }
    }

    // Update SectionSelectionType
    if (["optionalSection", "sectionSelectionType"].includes(keyName)) {
      let updateSectionSelectionType = "";

      if (keyName === "optionalSection") {
        if (keyData && sectionIdx === null) {
          updateSectionSelectionType = "single";
          updatedTable.sectionSelectionType = "single";

          updatedTable.sections.forEach((section, sectionIndex) => {
            if (section.optionalSection) updatedTable.sections[sectionIndex].optionalSection = false;
          });
        } else if (!keyData && sectionIdx === null) {
          updatedTable.sections.forEach(section => (section.active = true));
        } else if (sectionIdx) {
          updateSectionSelectionType = "multiple";
          updatedTable.sections[sectionIdx].sectionSelectionType = "multiple";
        }
      }

      if (keyData === "single" || updateSectionSelectionType === "single") {
        // For "single" type, set first item to active: true and others to active: false
        updatedTable.sections.forEach((section, sectionIndex) => (section.active = sectionIndex === 0));
      } else if (keyData === "multiple" || updateSectionSelectionType === "multiple") {
        // For "multiple" type, set all items to active: true
        if (sectionIdx) {
          updatedTable.sections[sectionIdx].active = true;
        } else updatedTable.sections.forEach(section => (section.active = true));
      }
    }

    if (["itemSelectionType", "optionalItem"].includes(keyName)) {
      let updateSectionSelectionType = "";

      if (keyName === "optionalItem") {
        if (keyData && itemIdx === null) {
          updateSectionSelectionType = "single";
          updatedTable.sections[sectionIdx].itemSelectionType = "single";
        } else {
          updateSectionSelectionType = "multiple";
          updatedTable.sections[sectionIdx].itemSelectionType = "multiple";

          if (typeof itemIdx === "number") {
            updatedTable.sections[sectionIdx].items[itemIdx].itemSelectionType = "multiple";
          }
        }
      }

      if (keyData === "single" || updateSectionSelectionType === "single") {
        updatedTable.sections[sectionIdx].items.forEach((item, itemIndex) => {
          item.active = itemIndex === 0;
          item.itemSelectionType = "single";
        });
      } else if (keyData === "multiple" || updateSectionSelectionType === "multiple") {
        updatedTable.sections[sectionIdx].items.forEach(item => {
          item.active = true;
          item.itemSelectionType = "multiple";
        });
      }
    }
    setTableConfigData(updatedTable);
  };

  const addSectionItem = (type, sectionIdx) => {
    // Add item/section
    let updatedTable = { ...tableConfigData };
    if (type === "item") {
      updatedTable.sections[sectionIdx].items.push({
        active:
          updatedTable?.sections[sectionIdx].optionalItem &&
          updatedTable?.sections[sectionIdx]?.itemSelectionType == "single"
            ? false
            : true,
        optionalItem: false,
        editableQuantity: false,
        minQty: 0,
        maxQty: 10,
        data: {
          id: "",
          item_name: "",
          unit_price: 0,
          quantity: 0,
          discountType: "%",
          discount: 0,
          tax: [],
          amount: 0,
          item_code: "",
          description: "",
        },
      });
    } else {
      updatedTable.sections.push({
        name: "Section Name",
        total: 0.0,
        optionalSection: false,
        optionalItem: false,
        itemSelectionType: "", // single/multiple
        active: updatedTable?.optionalSection && updatedTable?.sectionSelectionType === "single" ? false : true,

        items: [
          {
            active: true,
            optionalItem: false,
            editableQuantity: false,
            minQty: 0,
            maxQty: 10,
            data: {
              id: "",
              item_name: "",
              unit_price: 0,
              quantity: 0,
              discountType: "%",
              discount: 0,
              tax: [],
              amount: 0,
              item_code: "",
              description: "",
            },
          },
        ],
      });
    }
    setTableConfigData(updatedTable);
  };

  const deleteSectionItem = (type, sectionIdx, itemIdx) => {
    // Addt item/section
    let updatedTable = { ...tableConfigData };
    if (type === "section") {
      updatedTable.sections.splice(sectionIdx, 1);
    } else {
      updatedTable.sections[sectionIdx].items.splice(itemIdx, 1);
    }
    // Update table config data
    setTableConfigData(updatedTable);
  };

  const moveSection = (sectionIdx, position, sectionsArray) => {
    // Check if the section can be moved up or down
    if (
      (position === "up" && sectionIdx === 0) || // Cannot move the first section up
      (position === "down" && sectionIdx === tableConfigData.sections.length - 1) // Cannot move the last section down
    ) {
      return; // Exit the function if the move is invalid
    }
    let updatedTable = { ...tableConfigData };

    // Calculate the new index based on the position
    const newIndex = position === "up" ? sectionIdx - 1 : sectionIdx + 1;

    // Swap the sections in the array
    [updatedTable.sections[sectionIdx], updatedTable.sections[newIndex]] = [
      updatedTable.sections[newIndex],
      updatedTable.sections[sectionIdx],
    ];
    setTableConfigData(updatedTable);
  };

  const normalizeTaxForTable = (tax, taxList) => {
    if (!tax) return [];

    // For Old records
    if (typeof tax === "string") {
      return tax.split(",");
    }

    // For New Records
    if (Array.isArray(tax)) {
      return tax
        .map(id => {
          const matched = taxList.find(t => t._id === id);
          return matched ? `${matched.name} ${matched.rate}%` : null;
        })
        .filter(Boolean);
    }

    return [];
  };

  const selectPricigCurrency = e => {
    setCurrentCurrency(e);

    if (currentCurrency?.code === e?.code) return;

    /**
     * Below code to change
     * - Individual items discount type symbol
     * - One time discount type symbol
     */
    let updatedTable = { ...tableConfigData };
    updatedTable.currency = e.name;
    if (updatedTable.oneLineDiscountType !== "%") {
      updatedTable.oneLineDiscountType = e.symbol;
      updatedTable.oneLineDiscountValue = 0;
      setDiscountOptions({ ...discountOptions, oneLineDiscountValue: 0 });
    }
    // Update individual item data
    updatedTable?.sections?.forEach(section => {
      section.items?.forEach(item => {
        if (item?.data?.id) {
          let isPriceAvailable = itemList.find(listInfo => listInfo._id === item.data.id)?.pricing?.[e.name];
          if (isPriceAvailable) {
            item.data.unit_price = parseFloat(isPriceAvailable.unit_price);
            item.data.tax = normalizeTaxForTable(isPriceAvailable.tax, proposalModuleListInfo?.taxes?.data);
          } else {
            item.data.unit_price = 0;
            item.data.tax = [];
          }
        } else {
          item.data.unit_price = 0;
          item.data.tax = [];
        }

        if (item.data?.discountType !== "%") {
          item.data.discountType = e.symbol;
          item.data.discount = 0;
        }
      });
    });

    setTableConfigData(updatedTable);
  };

  const updateSectionItem = (sectionIdx, itemIdx, itemInfo) => {
    let updatedTable = { ...tableConfigData };
    updatedTable.sections[sectionIdx].items[itemIdx].data = {
      ...updatedTable.sections[sectionIdx].items[itemIdx].data,
      id: itemInfo._id,
      item_name: { name: itemInfo.name },
      quantity: 1,
      discount: "",
      item_code: itemInfo.code,
      images: itemInfo?.images || [],
      usage_unit: itemInfo.usage_unit || "",
      billing_frequency: itemInfo?.billing_frequency || "",
      description: itemInfo.description,
    };
    let isPriceAvailable = itemInfo?.pricing?.[currentCurrency.name];
    if (isPriceAvailable) {
      updatedTable.sections[sectionIdx].items[itemIdx].data.unit_price = isPriceAvailable.unit_price;

      updatedTable.sections[sectionIdx].items[itemIdx].data.tax = normalizeTaxForTable(
        isPriceAvailable?.tax,
        proposalModuleListInfo?.taxes?.data
      );
    } else {
      updatedTable.sections[sectionIdx].items[itemIdx].data.unit_price = 0;
      updatedTable.sections[sectionIdx].items[itemIdx].data.tax = [];
    }
    setTableConfigData(updatedTable);
  };

  const updateSectionValue = (updates, sectionIdx, itemIdx) => {
    if (typeof sectionIdx !== "number" || typeof itemIdx !== "number") return;

    let updatedTable = { ...tableConfigData };
    const section = updatedTable.sections[sectionIdx];
    const item = section.items[itemIdx];

    Object.entries(updates).forEach(([slug, value]) => {
      if (["maxQty", "minQty"].includes(slug)) {
        item[slug] = parseFloat(value);
        if (item?.editableQuantity) {
          if (slug === "maxQty") {
            // Reset quantity if "greater than" max value
            if (item?.data?.quantity > parseFloat(value)) item.data.quantity = parseFloat(value);
            // Reset min value if min value is greater than input value
            if (parseFloat(value) < item.minQty) item.minQty = parseFloat(value);
          } else if (slug === "minQty") {
            // Reset quantity if "less than" min value
            if (item?.data?.quantity < parseFloat(value)) item.data.quantity = parseFloat(value);
            // Reset max value if max value is less than input value
            if (parseFloat(value) > item.maxQty) item.maxQty = parseFloat(value);
          }
        }
      } else {
        if (item.data[slug] !== value) {
          item.data[slug] = value;
        }
      }
    });

    setTableConfigData(updatedTable);
  };

  // To format Currency
  function formatCurrency(
    value,
    thousandsSeparator = ",",
    decimalSeparator = ".",
    decimalPlaces = 2,
    numberFormat = "standard"
  ) {
    let [integer, decimal] = Number(value)?.toFixed(decimalPlaces).split("."); // Split the number into integer and decimal parts

    if (numberFormat === "indian") {
      const lastThree = integer.slice(-3);
      const rest = integer.slice(0, -3);

      integer = rest
        ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, thousandsSeparator) + thousandsSeparator + lastThree
        : lastThree;
    } else {
      integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    }

    return decimal ? `${integer}${decimalSeparator}${decimal}` : integer;
  }

  // To convert string into separator
  function getSymbolFromName(name) {
    const nameToSymbolMapping = { comma: ",", period: ".", space: " " };
    return nameToSymbolMapping[name];
  }

  const handleEditClick = sectionIdx => {
    setIsEditable(!isEditable);

    // Focus and place cursor at end after state update
    setTimeout(() => {
      const editableDiv = document.getElementById(`section-Name-${sectionIdx}`);
      if (editableDiv) {
        editableDiv.focus();
        return;
        // Move cursor to end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(editableDiv);
        range.collapse(false); // false means collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 0);
  };

  const changeSectionName = e => {
    const maxLength = 40; // Set your desired max length
    if (e.currentTarget.textContent.length > maxLength) {
      e.currentTarget.textContent = e.currentTarget.textContent.substring(0, maxLength);
    }
  };

  const onKeyDownFn = e => {
    const maxLength = 40;
    const currentText = e.currentTarget.textContent;

    // Allow Ctrl+A (Select All), deletions, and navigation keys
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
    ];

    // Allow Ctrl+A (keyCode 65 with Ctrl pressed)
    const isCtrlA = (e.ctrlKey || e.metaKey) && e.key === "a";

    // Block input if maxLength reached (unless allowed key)
    if (currentText.length >= maxLength && !allowedKeys.includes(e.key) && !isCtrlA) {
      e.preventDefault();
    }

    // Prevent Enter key (optional)
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur(); // Optionally blur on Enter
    }
  };

  const handleBlur = (e, sectionIdx) => {
    let updatedTable = { ...tableConfigData };
    setIsEditable(!isEditable);

    if (e.target.textContent.length === 0) {
      e.target.textContent = updatedTable.sections[sectionIdx].name; // Revert to previous value
      return;
    }

    updatedTable.sections[sectionIdx].name = e.target.textContent;
    setTableConfigData(updatedTable);
  };

  const filterTaxes = detailedTaxes => {
    const result = [];

    for (const item of detailedTaxes) {
      // Extract the base name by removing the percentage at the end
      const baseName = item.name;
      // const baseName = item.name.replace(/\s*\d+%$/, "").trim();

      // Find matching tax in availableTaxes (case-insensitive and trimming whitespace)
      const matchedTax = availableTaxes.find(tax => tax.name.trim().toLowerCase() === baseName.toLowerCase());

      if (matchedTax && matchedTax.id) {
        result.push(matchedTax.id);
      }
    }
    return result;
  };

  const filterCurrencies = () => {
    const result = [];
    const matchedCurrency = currencyList.find(
      curr => curr.name.trim().toLowerCase() === defaultCurrency.name.toLowerCase()
    );

    if (matchedCurrency && matchedCurrency._id) {
      result.push(matchedCurrency._id);
    }

    return result;
  };

  const filterSections = sectionArray => {
    const uniqueIds = [];

    // Loop through each section
    sectionArray.forEach(section => {
      // Loop through each item in the section
      section.items.forEach(item => {
        // Get the id from item.data
        const id = item.data.id;

        // Add to array if not already present
        if (!uniqueIds.includes(id) && id) {
          uniqueIds.push(id);
        }
      });
    });

    return uniqueIds;
  };

  const insertTable = () => {
    let insertedTableData = { ...tableConfigData };

    insertedTableData.detailedTaxes = detailedTaxes.map(item => ({
      ...item,
      amount: formattedCurrency(item.amount),
    }));

    // Below code to set in-use proposal modules
    let inUseProposalModuleList = {
      items: filterSections(insertedTableData.sections),
      currencies: filterCurrencies(),
      taxes: filterTaxes(insertedTableData.detailedTaxes),
    };

    dispatch(setUsedProposalModule(companyId, { document_id: documentId }, inUseProposalModuleList));

    insertedTableData.grandTotal = formattedCurrency(insertedTableData.grandTotal);
    insertedTableData.subTotal = formattedCurrency(insertedTableData.subTotal);
    calculatedSections.forEach((section, secIdx) => {
      insertedTableData.sections[secIdx].total = formattedCurrency(section.total);
      section.items.forEach((itemInfo, itemIdx) => {
        insertedTableData.sections[secIdx].items[itemIdx].data.amount = formattedCurrency(itemInfo.amount);
      });
    });
    setInsertedHtmlData({ ...insertedTableData });
    if (htmlMode) setInsertHtmlMode(true);

    // Use this htmlString for insert
    if (!editTable) {
      const htmlString = ReactDOMServer.renderToString(
        <CanvasHtmlData
          canvasConfigData={insertedTableData}
          formattedCurrency={formattedCurrency}
          usageUnitData={proposalModuleListInfo?.usage_units?.data || []}
        />
      );
      const elem = document.createElement("div");
      elem.innerHTML = htmlString;
      elem.setAttribute("data-currency", insertedTableData.currency);

      // add pheudo elem in editor
      const parent = document.getElementById(metadata.activeBlockId);
      const pheudoElem = document.createElement("div");
      pheudoElem.id = "pheudoElem";
      pheudoElem.style.width = "700px";
      pheudoElem.innerHTML = htmlString;
      parent.appendChild(pheudoElem);

      // insert into editor
      setTimeout(() => {
        const jsonString = JSON.stringify(insertedTableData);
        let paramObj = { "data-table-config": jsonString };
        let dataAttr = { ...widgetConfig[PRICING_TABLE].dataAttr, ...paramObj };
        let offsetHeight = document.querySelector("#pheudoElem .pricingTable_editor")?.offsetHeight;
        let offsetWidth = 700;
        let scale = 1242 / offsetWidth; // 1242 is page width(which is static)
        document.querySelector("#pheudoElem").remove();

        setCustomCss({
          transform: `scale(${scale})`,
          width: `${offsetWidth}px`,
          height: `${offsetHeight}px`,
        }); // set width height to inner div
        setWidgetWidth(offsetWidth * scale); // set width  to main div
        setWidgetHeight(offsetHeight * scale); // set height to main div
        setAssetInnerContent(elem);
        setDataParam(dataAttr);
        setAddWidgetClicked(true);
      }, 50);
    } else {
      const jsonString = JSON.stringify(insertedTableData);
      const htmlString = ReactDOMServer.renderToString(
        <CanvasHtmlData
          canvasConfigData={insertedTableData}
          formattedCurrency={formattedCurrency}
          usageUnitData={proposalModuleListInfo?.usage_units?.data || []}
        />
      );
      const elem = document.createElement("div");
      elem.innerHTML = htmlString;
      document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).innerHTML = elem.innerHTML;
      document
        .querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`)
        .setAttribute("data-currency", insertedTableData.currency);

      // add pheudo elem in editor
      const parent = document.getElementById(metadata.activeBlockId);
      const pheudoElem = document.createElement("div");
      pheudoElem.id = "pheudoElem";
      pheudoElem.style.width = document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.width;
      pheudoElem.innerHTML = htmlString;
      parent.appendChild(pheudoElem);

      setTimeout(() => {
        let offsetHeight = document.querySelector("#pheudoElem .pricingTable_editor")?.offsetHeight;
        let offsetWidth = parseFloat(
          document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.width
        );
        let scale = parseFloat(
          document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.transform.split("(")[1]
        );
        document.querySelector("#pheudoElem").remove();
        document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.width = `${offsetWidth}px`;
        document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.height = `${offsetHeight}px`;
        document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.transform = `scale(${scale})`;

        const targetWidgetIndex = widgets.findIndex(widget => widget.id === metadata.activeWidgetId[0]);

        let newArray = Object.assign([...widgets]);
        newArray = Object.assign([...newArray], {
          [targetWidgetIndex]: {
            ...newArray[targetWidgetIndex],
            data: {
              ...newArray[targetWidgetIndex].data,
              "data-table-config": jsonString,
            },
            style: {
              ...newArray[targetWidgetIndex].style,
              width: `${offsetWidth * scale}px`,
              height: `${offsetHeight * scale}px`,
            },
            innerHTML: document.getElementById(metadata.activeWidgetId[0]).innerHTML,
          },
        });
        updateWidgets(newArray);
      }, 50);

      setTimeout(() => {
        close();
      }, 100);
    }
  };

  const formattedCurrency = value => {
    if (!currentCurrency?.name) return;

    const currencyValue = formatCurrency(
      value,
      getSymbolFromName(currentCurrency?.thousand_seperator),
      getSymbolFromName(currentCurrency?.decimal_seperator),
      currentCurrency?.decimal_places,
      currentCurrency?.number_format
    );
    return `${currentCurrency?.symbol} ${currencyValue}`;
  };

  const doUndo = () => {
    setIsUndoRedo(true);
    undo();
  };
  const doRedo = () => {
    setIsUndoRedo(true);
    redo();
  };

  useEffect(() => {
    moduleList.forEach(moduleName => {
      fetchRecords(moduleName, { page: 1, limit: 50 });
    });
  }, []);

  useEffect(() => {
    if (currentCurrency && !disableCurrencyUpdate) {
      let updatedTable = { ...tableConfigData };
      updatedTable.currency = currentCurrency.name;
      setTableConfigData(updatedTable);
      setDisableCurrencyUpdate(true);
    }
  }, [currentCurrency]);

  useEffect(() => {
    const hasEmptyItemName = tableConfigData.sections.some(section =>
      section.items.some(item => item.data.item_name === "")
    );
    setDisableInsert(hasEmptyItemName);

    setDiscountOptions({
      ...discountOptions,
      oneLineDiscountValue: tableConfigData.oneLineDiscountValue,
      oneLineDiscount: tableConfigData.oneLineDiscount,
      oneLineDiscountType: tableConfigData.oneLineDiscountType,
      lineItemDiscount: tableConfigData.lineItemDiscount,
    });
    if (currentCurrency && currentCurrency.name !== tableConfigData?.currency) {
      let filteredCurrency = currencyList.find(currency => currency.name === tableConfigData?.currency);
      setDefaultCurrency({ name: filteredCurrency?.name });
      setCurrentCurrency(filteredCurrency);
    }
  }, [tableConfigData]);

  useEffect(() => {
    if (tableConfigData.currency === "") return;

    let updatedTable = { ...tableConfigData };
    if (grandTotal || grandTotal === 0) updatedTable.grandTotal = grandTotal;
    if (grandSubtotal || grandSubtotal === 0) updatedTable.subTotal = grandSubtotal;
    if (oneLineDiscountAmount || oneLineDiscountAmount === 0) {
      let calculatedOnlineDiscount = formattedCurrency(oneLineDiscountAmount);
      if (calculatedOnlineDiscount) {
        updatedTable.oneLineDiscountAmount = calculatedOnlineDiscount;
      }
    }
    setTableConfigData(updatedTable, { trackInUndo: false, ignoreUpdate: isUndoRedo });
    if (isUndoRedo) setIsUndoRedo(false);
  }, [grandTotal, grandSubtotal, oneLineDiscountAmount]);

  // Close pricing table modal after innserted to canvas
  useEffect(() => {
    if (getNewWidgetObject?.id && !htmlMode) close();
  }, [getNewWidgetObject]);

  return (
    <>
      {!insertHtmlMode && (
        <div className="table-modal-wrap">
          <div className="d-flex justify-content-between modal-header">
            <h4 className={cx(style["fw-7"], style["mb-0"])}>Pricing Table</h4>
            <div className={style["header-center"]}>
              <ModalFlashMessage flashMessage={customMsg} />
            </div>
            <ul className={style["d-flex"]}>
              <li
                className={cx(style["custom-tooltip"], { [style["disabled"]]: !canUndo })}
                onClick={doUndo}
                disabled={!canUndo}>
                <span className={cx(style["cursor-pointer"], style["d-flex"], style["undo"])}>
                  <Icon icon="ui-undo" />
                </span>
                <div className={cx(style["custom-tooltip-content"], style["bottom"])}>Undo</div>
              </li>
              <li
                className={cx(style["custom-tooltip"], { [style["disabled"]]: !canRedo })}
                onClick={doRedo}
                disabled={!canRedo}>
                <span className={cx(style["cursor-pointer"], style["d-flex"], style["redo"])}>
                  <Icon icon="ui-redo" />
                </span>
                <div className={cx(style["custom-tooltip-content"], style["bottom"])}>Redo</div>
              </li>
              <li>
                <Input
                  cssModule={style}
                  returnType="formGroup"
                  type="dropdown"
                  classNamePrefix="select"
                  dropDownMenuClass="text-capitalize"
                  className="input-with-link pricing-table"
                  getOptionValue={option => option?.name}
                  getOptionLabel={option => <span>{option?.name}</span>}
                  options={currencyList}
                  defaultValue={defaultCurrency}
                  updateState={e => selectPricigCurrency(e)}
                  closeOnScroll={true}
                />
              </li>

              {/* Below dropdown for Settings */}
              <li className={cx(style["custom-tooltip"], style["settings-dropdown"])}>
                <Dropdown isOpen={isOpen} toggle={toggle} className={cx(style["p-2"], style["btn-square-icon"])}>
                  <DropdownToggle tag="a" className={cx(style["dropdown-toggle"], style["custom-tooltip"])}>
                    <Icon icon="ui-settings" />
                    <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>Settings</div>
                  </DropdownToggle>
                  <DropdownMenu
                    right
                    className={cx(style["shadow"], style["border-0"], style["rounded-sm"], "custom-pricing-dropdown")}>
                    <DropdownItem tag="span">
                      <ToggleDropdownItem
                        toggleId={"showItemCode"}
                        label={"Show Item Code"}
                        checked={tableConfigData?.showItemCode}
                        selectfn={() => updateTableConfig({ showItemCode: !tableConfigData.showItemCode })}
                      />
                    </DropdownItem>
                    <DropdownItem header>Discount</DropdownItem>
                    <DropdownItem tag="span">
                      <ToggleDropdownItem
                        toggleId={"lineItemDiscount"}
                        label={"Line-Item Discount"}
                        checked={tableConfigData?.lineItemDiscount}
                        selectfn={() => updateTableConfig({ lineItemDiscount: !tableConfigData.lineItemDiscount })}
                      />
                    </DropdownItem>
                    <DropdownItem tag="span">
                      <ToggleDropdownItem
                        toggleId={"onetimeDiscount"}
                        label={"One-time Discount"}
                        checked={tableConfigData?.oneLineDiscount}
                        selectfn={() =>
                          updateTableConfig({
                            oneLineDiscount: !tableConfigData.oneLineDiscount,
                            oneLineDiscountAmount: 0,
                            oneLineDiscountValue: 0,
                          })
                        }
                      />
                    </DropdownItem>
                    <DropdownItem header>Section</DropdownItem>
                    <DropdownItem tag="span">
                      <ToggleDropdownItem
                        toggleId={"optionalSection"}
                        label={"Optional Section"}
                        checked={tableConfigData?.optionalSection}
                        selectfn={() => updateTableConfig({ optionalSection: !tableConfigData.optionalSection })}
                      />
                    </DropdownItem>
                    {tableConfigData.optionalSection && (
                      <>
                        <DropdownItem className={style["optional-radio"]} tag="span">
                          <Input
                            type="radio"
                            id="singleOpctionMain"
                            radioClass="radio-icon mb-0"
                            text="Single Selection"
                            checked={"single" == tableConfigData?.sectionSelectionType}
                            className={style["form-check-input"]}
                            onChange={() => updateTableConfig({ sectionSelectionType: "single" })}
                          />
                        </DropdownItem>
                        <DropdownItem className={style["optional-radio"]} tag="span">
                          <Input
                            type="radio"
                            id="multipleOpctionMain"
                            radioClass="radio-icon  mb-0"
                            text="Multiple Selection"
                            checked={"multiple" == tableConfigData?.sectionSelectionType}
                            className={style["form-check-input"]}
                            onChange={() => updateTableConfig({ sectionSelectionType: "multiple" })}
                          />
                        </DropdownItem>
                      </>
                    )}
                  </DropdownMenu>
                </Dropdown>
              </li>
              <li className={cx(style["expand-btn"], style["rounded"])} onClick={setFullScreen}>
                <Icon icon={modalFullScreen ? "ui-collapse" : "ui-expand"} />
              </li>
              <li className={cx(style["cross-modal"], style["rounded"])} onClick={close}>
                <Icon icon="ui-close" />
              </li>
            </ul>
          </div>

          <div
            className={cx(style["modal-body"], style["customScroll"], style["scroll-Y"])}
            style={{ height: getModalHeight(), maxHeight: "calc(84% - 2rem)" }}>
            {tableConfigData?.sections?.map((sectionInfo, sectionIdx) => (
              <SectionComponent
                key={sectionIdx}
                sectionInfo={sectionInfo}
                sectionIdx={sectionIdx}
                tableConfigData={tableConfigData}
                handleBlur={handleBlur}
                isEditable={isEditable}
                handleEditClick={handleEditClick}
                changeSectionName={changeSectionName}
                onKeyDownFn={onKeyDownFn}
                formattedCurrency={formattedCurrency}
                moveSection={moveSection}
                updateTableConfig={updateTableConfig}
                deleteSectionItem={deleteSectionItem}
                updateSectionItem={updateSectionItem}
                currentCurrency={currentCurrency}
                updateSectionValue={updateSectionValue}
                calculatedSections={calculatedSections}
                billingSuffixes={billingSuffixes}
                addSectionItem={addSectionItem}
                itemList={itemList}
                availableTaxes={availableTaxes}
                setTableConfigData={setTableConfigData}
                dropdownContainer={dropdownContainer}
                modalFullScreen={modalFullScreen}
                isLoadingItem={isLoadingItem}
                isDescriptionFocused={isDescriptionFocused}
                setIsDescriptionFocused={setIsDescriptionFocused}
                usageUnitData={proposalModuleListInfo?.usage_units?.data ?? []}
                setCustomMsg={setCustomMsg}
              />
            ))}

            <Row className={style["justify-content-between"]}>
              <Col>
                <div className={cx(style["add-section"])}>
                  <div className={cx(style["d-flex"], style["align-items-center"])}>
                    <a
                      className={cx(style["d-flex"], style["align-items-center"], style["color-33"])}
                      data-role="add-section"
                      onClick={() => {
                        if (isDescriptionFocused) return;
                        addSectionItem("section");
                      }}
                      tabIndex={0}>
                      <Icon icon="ui-plus" additionalclass={"me-2"} />
                      <span>Section</span>
                    </a>
                  </div>
                </div>
              </Col>
              <Col xs="auto">
                <Card
                  className={cx(style["subtotal-wrap"], { [style["add-discount"]]: tableConfigData?.oneLineDiscount })}>
                  <CardHeader className={cx(style["border-bottom-0"])}>
                    <div className={cx(style["d-flex"], style["justify-content-between"], style["content"])}>
                      <div className={style["left"]}>
                        <ul>
                          <li>Subtotal</li>
                          {tableConfigData?.oneLineDiscount && (
                            <li className={style["discount-field"]}>
                              <div className={cx(style["d-flex"], style["align-items-center"])}>
                                Discount
                                <div className={cx(style["ps-2"], style["ms-1"])}>
                                  <ItemFields
                                    fieldType="discount"
                                    itemValue={tableConfigData?.oneLineDiscountValue}
                                    slug={"oneLineDiscountValue"}
                                    updateSectionValue={updateSectionValue}
                                    isEnable={true}
                                    discountOptions={[currentCurrency?.symbol]}
                                    defaultDiscountType={tableConfigData?.oneLineDiscountType}
                                    updateTableConfig={updateTableConfig}
                                    maxLength={7}
                                    currData={tableConfigData}
                                  />
                                </div>
                              </div>
                            </li>
                          )}
                          {detailedTaxes?.map((tax, taxIdx) => (
                            <li key={taxIdx}>{tax.name}</li>
                          ))}

                          <li className={cx(style["fw-7"], style["font-base"])}>Grand Total</li>
                        </ul>
                      </div>
                      <div className={style["right"]}>
                        <ul>
                          <li>{formattedCurrency(tableConfigData?.subTotal)}</li>
                          {tableConfigData?.oneLineDiscount && (
                            <li className={style["discount-field"]}>
                              <div>{formattedCurrency(oneLineDiscountAmount)}</div>
                            </li>
                          )}
                          {detailedTaxes?.map((tax, taxIdx) => (
                            <li key={taxIdx}>{formattedCurrency(tax?.amount)}</li>
                          ))}

                          <li className={cx(style["fw-7"], style["font-base"])}>
                            {formattedCurrency(tableConfigData?.grandTotal)}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </Col>
            </Row>
          </div>

          <div className={style["modal-footer"]}>
            <button
              type="button"
              onClick={insertTable}
              className={cx(style["btn"], style["btn-primary"])}
              disabled={disableInsert}>
              {!editTable ? "Insert" : "Update"}
            </button>
          </div>
        </div>
      )}

      {/* below section need to delete after insert html page design done */}
      {insertHtmlMode && (
        <Modal
          size="lg"
          showModal={insertHtmlMode}
          component={CanvasHtmlData}
          canvasConfigData={insertedHtmlData}
          setShowModal={setInsertHtmlMode}
          formattedCurrency={formattedCurrency}
          usageUnitData={proposalModuleListInfo?.usage_units?.data || []}
        />
      )}
    </>
  );
};

export default PricingTable;
